/**
 * Attendance statistics + ICS calendar feed contracts (milestone 6).
 *
 * Attendance rule (one "cell" per counted event × member):
 *  - counted events: status 'scheduled', counts_attendance = 1, already ended (ends_at < now),
 *    starting inside the requested date range;
 *  - counted members: status 'active' (on leave / invited / inactive / erased are left out),
 *    joined_on ≤ the event's date, and a section the event counts (attendanceCountsSection():
 *    singers when the event targets the whole choir or their section, conductor / musicians only
 *    when the event targets them explicitly);
 *  - result: the roster override when set, else 'present' when checked in, else 'excused' when the
 *    RSVP is "no", else 'absent'. pct = present / total (percent, one decimal; null when total = 0).
 */
import type { Section } from '../audience';
import type { EventKind } from './events';

export type AttendanceResult = 'present' | 'excused' | 'absent';

export interface AttendanceCounts {
  present: number;
  excused: number;
  absent: number;
  total: number;
  /** present / total in percent (0–100, one decimal); null when total = 0. */
  pct: number | null;
}

export interface MemberAttendanceDTO extends AttendanceCounts {
  userId: string;
  fullName: string;
  section: Section | null;
  avatarRev: number;
}

export interface SectionAttendanceDTO extends AttendanceCounts {
  section: Section;
  /** Members of the section with at least one counted event. */
  members: number;
}

export interface EventAttendanceDTO {
  eventId: string;
  title: string;
  kind: EventKind;
  startsAt: number;
  present: number;
  excused: number;
  absent: number;
  total: number;
}

/** GET /api/stats/attendance?from=YYYY-MM-DD&to=YYYY-MM-DD (editor+). Default range: the current season. */
export interface AttendanceStatsDTO {
  /** Effective range (both inclusive). */
  from: string;
  to: string;
  generatedAt: number;
  /** Members with at least one counted event in the range. */
  members: MemberAttendanceDTO[];
  /** Pooled per section (only sections with counted members), in SECTIONS order. */
  sections: SectionAttendanceDTO[];
  /** Pooled over all members. */
  overall: AttendanceCounts;
  /** Every counted event of the range, oldest first. */
  events: EventAttendanceDTO[];
}

export interface MemberEventAttendanceDTO {
  eventId: string;
  title: string;
  kind: EventKind;
  startsAt: number;
  endsAt: number;
  result: AttendanceResult;
  /** The result was set by the conductor on the roster. */
  overridden: boolean;
}

/**
 * GET /api/stats/attendance/:userId (editor+, or the member themself) and GET /api/stats/me
 * (signed-in user; both accept ?from&to, default = the current season).
 */
export interface MemberAttendanceDetailDTO {
  from: string;
  to: string;
  generatedAt: number;
  member: MemberAttendanceDTO;
  /** Counted events for this member, oldest first. */
  events: MemberEventAttendanceDTO[];
}

/** GET /api/cal/token → is a calendar subscription link active for me? */
export interface CalendarFeedStatusDTO {
  active: boolean;
}

/**
 * POST /api/cal/token → a NEW subscription link (any previous link stops working). The token is
 * only ever shown in this response; the server keeps its SHA-256.
 */
export interface CalendarFeedLinkDTO {
  active: true;
  /** https://host/api/cal/<token>.ics (copy / share / Google Calendar "From URL"). */
  httpsUrl: string;
  /** webcal://host/api/cal/<token>.ics (opens the calendar app's subscribe dialog on iPad/iPhone/Mac). */
  webcalUrl: string;
}

/** Longest range accepted by the stats endpoints (about three seasons). */
export const STATS_MAX_RANGE_DAYS = 1100;

/** A season (starfsár) runs from 1 September to 31 August. */
export interface DateRange {
  from: string;
  to: string;
}

/**
 * The season containing `date` ('YYYY-MM-DD'), shifted by `offset` seasons (−1 = last season).
 * 2026-10-14 → 2026-09-01 … 2027-08-31; 2027-03-01 → 2026-09-01 … 2027-08-31.
 */
export function seasonRange(date: string, offset = 0): DateRange {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const start = (m >= 9 ? y : y - 1) + offset;
  return { from: `${start}-09-01`, to: `${start + 1}-08-31` };
}

/** pct as defined above: percent with one decimal, null when nothing was counted. */
export function attendancePct(present: number, total: number): number | null {
  return total > 0 ? Math.round((present * 1000) / total) / 10 : null;
}
